import Grid from "components/primitives/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "hooks";
import useLink from "hooks/useLink";
import * as Components from "./SettingsMenu.components";
import useCanDo from "hooks/useCanDo";
import { useEffect, useMemo } from "react";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import useOrganisationActions from "providers/OrganisationProvider/useOrganisationActions";
import { useTranslation } from "react-i18next";
import SettingsMenuItem from "./SettingsMenuItem";

interface RouteItem {
  label: string;
  route?: string;
  component?: React.ForwardRefExoticComponent<React.RefAttributes<HTMLAnchorElement>>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

interface SettingsMenuProps {
  children: React.ReactNode;
}

function SettingsMenu(props: SettingsMenuProps) {
  const [, { onSignOut }] = useUserContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const renderLink = useLink();
  const canDo = useCanDo();
  // TODO: (0S-4710) Replace with AccountInfoProvider
  const organisation = useOrganisation();
  const organisationActions = useOrganisationActions();

  useEffect(() => {
    organisationActions.get();
  }, [organisationActions]);

  const coachesEnabled = Boolean(organisation.accountInfo.coachLimit);
  // END TODO

  const items: RouteItem[] = useMemo(
    () =>
      [
        { label: t("settings.account_page"), route: "", component: renderLink("/settings") },
        canDo("group.any") &&
          canDo("coach.any") &&
          coachesEnabled && {
            label: t("settings.coaches_page"),
            component: renderLink("/settings/coaches"),
            route: "coaches",
          },
        canDo("athlete.invite") && {
          label: t("settings.athlete_invite_page"),
          component: renderLink("/settings/athlete-invite"),
          route: "athlete-invite",
        },
        {
          label: t("settings.athlete_monitoring_page"),
          component: renderLink("/settings/athlete-monitoring"),
          route: "athlete-monitoring",
        },
        canDo("athlete.archive") && {
          label: t("settings.archived_athletes_page"),
          component: renderLink("/settings/athlete-archive"),
          route: "athlete-archive",
        },
        canDo("subscription.any") && {
          label: t("settings.subscription_page"),
          component: renderLink("/settings/subscription"),
          route: "subscription",
        },
        { label: t("settings.preferences_page"), component: renderLink("/settings/preferences"), route: "preferences" },
        { label: "", disabled: true },
        {
          label: t("settings.sign_out"),
          onClick: async function handleSignout() {
            await onSignOut();
            navigate("/", { replace: true });
          },
        },
      ].filter(Boolean) as RouteItem[],
    [t, canDo, navigate, renderLink, onSignOut, coachesEnabled]
  );

  return (
    <Grid container minHeight="100%">
      <Components.VerticalDividerGrid size={{ xs: 2.25 }} minHeight="100%">
        <Box sx={{ p: 3, pb: 0 }}>
          <Typography variant="h1" gutterBottom>
            {t("settings.page_title")}
          </Typography>
        </Box>
        <List>
          {items.map((item) => (
            <SettingsMenuItem key={item.label} {...item} />
          ))}
        </List>
      </Components.VerticalDividerGrid>
      <Grid size="grow" sx={{ height: "100%", overflowY: "auto" }}>
        {props.children}
      </Grid>
    </Grid>
  );
}

export default SettingsMenu;
